import {setOrientationRoutings} from 'actions/index.js';

export default superclass => class extends superclass {
    // mapping between module id => [...module ids],
    // for each orientation
    routings = {
        0: {},
        1: {},
    };

    // free modules just play as normal. important that when you
    // have something going you can then create a new module
    // and just play around free form
    freeformModules = [];

    // various caches for functions
    _getOutputForModule = {};
    _getModulesRouted = {};
    _getModulesRoutedIn = {};
    _isModuleRouted = {};
    _getModule = {};
    _outputModules = undefined;

    get outputModules() {
        if (this._outputModules) {
            return this._outputModules;
        }
        let out0 = this.routings[0];
        let out1 = this.routings[1];
        this._outputModules = Array.from(new Set([
            ...
                Object.values(out0)
                .flatMap(x => x),
            ...
                Object.values(out1)
                .flatMap(x => x)
        ]));
        return this._outputModules;
    }

    // returns the modules set to be the output of (i.e. sound
    // generators) for a given module, based on the routing
    // configuration
    getOutputForModule(module) {
        let key = module ? module.id : 'none';
        if (this._getOutputForModule[key]) {
            return this._getOutputForModule[key];
        }

        if (this.isEmpty || this.isFreeform(module)) {
            // by default just use the module itself
            this._getOutputForModule[key] = [module];
            return [module];
        }

        // otherwise find the orientation at hand
        let orientation = Object.values(this.currentAlgorithms)[0].orientation;
        this._getOutputForModule[key] = this.routings[orientation][module.id] || [];
        return this._getOutputForModule[key];
    }

    getModulesRoutedIn(orientation) {
        if (this._getModulesRoutedIn[orientation]) {
            return this._getModulesRoutedIn[orientation];
        }
        this._getModulesRoutedIn[orientation] = Object
            .keys(this.routings[orientation])
            .map(moduleId => this.getModule(moduleId));

        return this._getModulesRoutedIn[orientation];
    }

    getModulesRouted(orientation) {
        if (this._getModulesRouted[orientation]) {
            return this._getModulesRouted[orientation];
        }
        this._getModulesRouted[orientation] = Object
            .values(this.routings[orientation])
            .flatMap(x => x);
        return this._getModulesRouted[orientation];
    }

    isModuleRouted(module, orientation) {
        let key = module ? module.id+'.'+orientation : orientation;
        if (this._isModuleRouted[key]) {
            return this._isModuleRouted[key];
        }
        if (!module) {
            this._isModuleRouted[key] = true;
            return true;
        }
        let result = Object.keys(this.routings[orientation])
            .includes(module.id);
        this._isModuleRouted[key] = result;
        return result;
    }

    get modules() {
        return this.sequencerEngine.subSequences;
    }

    getModule(id) {
        if (this._getModule[id]) {
            return this._getModule[id];
        }
        this._getModule[id]  = this.modules.find(
            module => module.id === id);
        return this._getModule[id];
    }

    connectModules(orientation, moduleA, moduleB) {
        let o = this.routings[orientation];
        if (!moduleA) {
            return;
        }
        let routingsFrom = o[moduleA.id] || [];
        routingsFrom.push(moduleB);
        o[moduleA.id] = routingsFrom;
        this.updateRoutableRedux();
    }

    disconnectModules(orientation, moduleA, moduleB) {
        let o = this.routings[orientation];
        let routingsFrom = o[moduleA.id] || [];
        routingsFrom = routingsFrom.filter(
            m => m !== moduleB);
        o[moduleA.id] = routingsFrom;
        this.updateRoutableRedux();
    }

    isFreeform(module) {
        return this.freeformModules.includes(module);
    }

    connectToFreeform(module) {
        this.freeformModules.push(module);
        this.updateRoutableRedux();
    }

    disconnectFromFreeform(module) {
        this.freeformModules =
            this.freeformModules.filter(
                m => m !== module);
        this.updateRoutableRedux();
    }

    clearRoutingCache() {
        this._getOutputForModule = {};
        this._getModulesRouted = {};
        this._isModuleRouted = {};
        this._outputModules = undefined;
        this._getModulesRoutedIn = {};
    }

    updateRoutableRedux() {
        // clear cache
        this.clearRoutingCache();

        this.dispatch(
            setOrientationRoutings(
                {
                    ...
                        this.routings
                }));
                
    }

    getRoutingJson() {
        let json = {};
        for (let o in this.routings) {
            let _o = {};
            for (let from in this.routings[o]) {
                _o[from] = this.routings[o][from].map(x => x.id);
            }
            json[o] = _o;
        }
        return json;
    }

    fromRoutingJson(json) {
        for (let o in json) {
            o = parseInt(o);
            for (let from in json[o]) {
                let fromModule = this.getModule(from);
                json[o][from].forEach(
                    moduleId =>
                        this.connectModules(
                            o, fromModule, this.getModule(moduleId)));
            }
        }
    }
}
